import { animatePattern, definePattern, positive, randomAt } from './core'
import type { PatternDefinition, PatternLayer, PatternMark } from './types'

const rect = (width: number, height: number, fill: string, x = 0, y = 0): PatternMark => ({ kind: 'rect', width, height, fill, x, y })
const path = (d: string, stroke: string, strokeWidth: number): PatternMark => ({ kind: 'path', d, stroke, strokeWidth, fill: 'none' })
const pulse = (seconds: number, duration: number) => (1 - Math.cos(seconds * Math.PI * 2 / duration)) / 2

export function stripes({ spacing = 16, thickness = 3, angle = 45, color = 'currentColor' } = {}): PatternDefinition {
  positive(spacing, 'Stripe spacing')
  positive(thickness, 'Stripe thickness')
  return definePattern({ tile: { width: spacing, height: spacing, transform: `rotate(${angle})` }, marks: [rect(thickness, spacing, color)] })
}

export function dots({ spacing = 24, radius = 3, color = 'currentColor' } = {}): PatternDefinition {
  positive(spacing, 'Dot spacing')
  positive(radius, 'Dot radius')
  return definePattern({ tile: { width: spacing, height: spacing }, marks: [{ kind: 'circle', cx: spacing / 2, cy: spacing / 2, r: radius, fill: color }] })
}

export function unavailable({ spacing = 32, color = 'currentColor' } = {}): PatternDefinition {
  positive(spacing, 'Unavailable spacing')
  const ratio = spacing / 32
  return definePattern({ tile: { width: spacing, height: spacing }, marks: [8, 24].flatMap(center => [
    { kind: 'circle' as const, cx: center * ratio, cy: center * ratio, r: 7 * ratio, fill: 'none', stroke: color, strokeWidth: 2 * ratio },
    { ...path(`M${(center - 2) * ratio} ${(center - 2) * ratio}L${(center + 2) * ratio} ${(center + 2) * ratio}M${(center + 2) * ratio} ${(center - 2) * ratio}L${(center - 2) * ratio} ${(center + 2) * ratio}`, color, 2 * ratio), strokeLinecap: 'round' as const },
  ]) })
}

export function signalWeave(): PatternDefinition {
  const frame = (seconds: number): PatternLayer[] => [{
    tile: { width: 128, height: 64 },
    marks: Array.from({ length: 32 }, (_, index) => {
      const column = index % 8
      const row = Math.floor(index / 8)
      const phase = ((seconds + ((column + row * 2) % 8) * 0.4) % 3.2) / 3.2
      const peak = phase < 0.55 ? 0 : phase < 0.7 ? (phase - 0.55) / 0.15 : Math.max(0, 1 - (phase - 0.7) / 0.3)
      const x = 8 + column * 16
      const y = 8 + row * 16
      return { id: `signal-${index}`, kind: 'circle', cx: 0, cy: 0, r: 1.75, fill: 'currentColor', opacity: 0.12 + peak * 0.68, transform: `translate(${x} ${y}) scale(${1 + peak * 2} ${1 - peak * 0.2})` }
    }),
  }]
  return animatePattern(definePattern(...frame(0)), frame)
}

export function stripeWeave({ warp = '@warp', weft = '@weft', background = '@background', warpWidth = 10, weftWidth = 8 } = {}): PatternDefinition {
  const layer: PatternLayer = { tile: { width: 56, height: 56 }, marks: [
    rect(56, 56, background),
    path('M-14 14L14-14M0 56L56 0M42 70L70 42', warp, warpWidth),
    path('M-14 42L42-14M14 70L70 14', weft, weftWidth),
  ] }
  return animatePattern(definePattern(layer), seconds => [{ ...layer, tile: { width: 56, height: 56, transform: `translate(${seconds % 2.8 / 2.8 * 56} 0)` } }])
}

export function confetti(): PatternDefinition {
  const circles = [[14, 14, 10, 4, 2.2], [54, 18, 8, 3, 1.8], [22, 50, 12, 3, 2.4], [58, 54, 10, 3, 2]] as const
  const frame = (seconds: number): PatternLayer[] => [{ tile: { width: 72, height: 72 }, marks: [
    rect(72, 72, '@background'),
    ...circles.map(([cx, cy, r, delta, duration], i): PatternMark => ({ kind: 'circle', cx, cy, r: r + delta * pulse(seconds, duration), fill: `@color${i + 1}` })),
  ] }]
  return animatePattern(definePattern(...frame(0)), frame)
}

/** The source gallery's displayed rings are static, concentric, and deliberately off-center. */
export function radialRings(): PatternDefinition {
  return definePattern({ marks: [396, 312, 228, 144, 60].map((r, index) => ({ kind: 'circle', cx: 2, cy: 68, r, fill: `@color${index % 4 + 1}` })) })
}

export function waves({ amplitude = 14, thickness = 12, speed = 1 } = {}): PatternDefinition {
  const frame = (seconds: number): PatternLayer[] => {
    const bend = amplitude * Math.cos(seconds * speed * Math.PI * 2 / 2.4)
    return [{ tile: { width: 96, height: 64, transform: `translate(${seconds * speed % 4 / 4 * 48} 0)` }, marks: [
      rect(96, 64, '@background'),
      ...[18, 46].map((y, i): PatternMark => ({
        id: `wave-${i}`, kind: 'path', fill: 'none',
        d: `M-24 ${y}C-8 ${y - bend} 8 ${y - bend} 24 ${y}S56 ${y + bend} 72 ${y}S104 ${y - bend} 120 ${y}`, stroke: `@color${i + 1}`, strokeWidth: thickness,
        strokeLinecap: 'round',
      })),
    ] }]
  }
  return animatePattern(definePattern(...frame(0)), frame)
}

export function pixelSteps({ size = 32 } = {}): PatternDefinition {
  positive(size, 'Pixel size')
  const colors = [1, 2, 3, 4, 3, 5, 2, 1, 5]
  const next = [2, 5, 1, 3, 2, 3, 1, 5, 4]
  const durations = [3.2, 2.5, 2.9, 3, 2.4, 2.6, 2.8, 2.2, 3.4]
  const frame = (seconds: number): PatternLayer[] => [{ tile: { width: size * 3, height: size * 3 }, marks: colors.flatMap((color, i) => [
    rect(size, size, `@color${color}`, i % 3 * size, Math.floor(i / 3) * size),
    { ...rect(size, size, `@color${next[i]}`, i % 3 * size, Math.floor(i / 3) * size), opacity: pulse(seconds, durations[i]) },
  ]) }]
  return animatePattern(definePattern(...frame(0)), frame)
}

export function nestedHex(): PatternDefinition {
  return definePattern({ transform: 'translate(-14 -14) rotate(50 160 82)', marks: [
    'M160 -92L364 26V262L160 380L-44 262V26Z',
    'M160 -44L320 48V232L160 324L0 232V48Z',
    'M160 -4L286 68V212L160 284L34 212V68Z',
    'M160 28L258 84V196L160 252L62 196V84Z',
    'M160 54L236 98V182L160 226L84 182V98Z',
  ].map((d, i) => ({ kind: 'path', d, fill: i % 2 ? '@background' : '@accent' })) })
}

export function noise({ seed = 1, size = 4, columns = 32, rows = 16 } = {}): PatternDefinition {
  positive(size, 'Noise pixel size')
  if (!Number.isInteger(columns) || !Number.isInteger(rows) || columns < 1 || rows < 1 || columns * rows > 4096) throw new Error('Noise grid must contain 1–4096 cells with integer dimensions')
  const frame = (seconds: number): PatternLayer[] => [{ tile: { width: columns * size, height: rows * size }, marks: [
    rect(columns * size, rows * size, '@background'),
    ...Array.from({ length: columns * rows }, (_, i) => ({ ...rect(size, size, '@ink', i % columns * size, Math.floor(i / columns) * size), opacity: randomAt(seed + Math.floor(seconds * 12), i) })),
  ] }]
  return animatePattern(definePattern(...frame(0)), frame)
}

export function prismaticWeave(): PatternDefinition {
  const frame = (seconds: number): PatternLayer[] => [{ tile: { width: 64, height: 64 }, marks: [
    rect(64, 64, '@background'),
    path('M0 16H64M0 48H64', '@weft', 16),
    path('M0 10H64M0 42H64', '@weftEdge', 2),
    path('M16 0V64M48 0V64', '@shadow', 22),
    path('M16 0V64M48 0V64', '@warp', 16),
    path('M10 0V64M42 0V64', '@warpEdge', 2),
    { ...path('M16 0V64M48 0V64', '@warpLight', 3), strokeDasharray: '8 56', strokeDashoffset: -seconds / 6 * 64, opacity: 0.55 },
    path('M5 16H27M37 48H59', '@shadow', 22),
    path('M4 16H28M36 48H60', '@weft', 16),
    path('M4 10H28M36 42H60', '@weftEdge', 2),
    { ...path('M4 16H28M36 48H60', '@weftLight', 3), strokeDasharray: '8 56', strokeDashoffset: -(seconds + 3) / 6 * 64, opacity: 0.55 },
  ] }]
  return animatePattern(definePattern(...frame(0)), frame)
}

export function sphereBands(): PatternDefinition {
  return definePattern({
    transform: 'translate(160 82) rotate(-24) translate(-160 -82)',
    marks: [{ kind: 'circle', cx: 160, cy: 82, r: 60 }],
    cutouts: [-58, -48, -35, -20, -4, 12, 27, 40, 51].map(latitude => {
      const halfWidth = Math.sqrt(60 ** 2 - latitude ** 2)
      return path(`M${160 - halfWidth - 4} ${82 + latitude}Q160 ${82 + latitude + halfWidth * 0.65} ${160 + halfWidth + 4} ${82 + latitude}`, 'black', 4.5)
    }),
  })
}

/** Image effect composed through the same layer renderer; source URL is caller-owned. */
export function directionalSmear(href: string): PatternDefinition {
  const frame = (seconds: number): PatternLayer[] => {
    const phase = seconds % 12 / 3
    const x = phase < 2 ? 0.4 + 13.6 * Math.sin(phase * Math.PI / 2) ** 2 : 0.4
    const y = phase >= 2 ? 0.4 + 13.6 * Math.sin((phase - 2) * Math.PI / 2) ** 2 : 0.4
    return [{ marks: [{ kind: 'image', href, width: 320, height: 200 }], blur: [x, y] }]
  }
  return animatePattern(definePattern(...frame(0)), frame)
}
